// src/hooks/useFilters.ts
import { useState, useCallback, useMemo } from 'react';
import { Gathering, Space } from '../../../types';

// Define the filter state interface
export interface FilterState {
  spaceId: string | null;
  dateFilterType: 'gathering' | 'submission'; // Which date field to filter by
  fromDate: Date | null;
  toDate: Date | null;
  searchQuery: string | null;
}

// Helper to get default fromDate (30 days ago)
const getDefaultFromDate = (): Date => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  date.setHours(0, 0, 0, 0); // Start of day
  return date;
};

// Default filter state - 30 days ago to limit data load
const defaultFilterState: FilterState = {
  spaceId: null,
  dateFilterType: 'gathering', // Default to gathering date
  fromDate: getDefaultFromDate(),
  toDate: null,
  searchQuery: null
};

export const useFilters = (initialState: Partial<FilterState> = {}) => {
  // Combine default state with any initial state provided
  const [filters, setFilters] = useState<FilterState>({
    ...defaultFilterState,
    ...initialState
  });

  // Set space filter
  const setSpaceFilter = useCallback((spaceId: string | null) => {
    setFilters(prev => ({ ...prev, spaceId }));
  }, []);

  // Set date range filter
  const setDateRangeFilter = useCallback((fromDate: Date | null, toDate: Date | null) => {
    setFilters(prev => ({ ...prev, fromDate, toDate }));
  }, []);

  // Set search filter
  const setSearchFilter = useCallback((searchQuery: string | null) => {
    setFilters(prev => ({ ...prev, searchQuery }));
  }, []);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setFilters(defaultFilterState);
  }, []);

  // Apply filters to a collection of gatherings
  const applyFilters = useCallback((gatherings: Gathering[]): Gathering[] => {
    return gatherings.filter(gathering => {
      // Apply space filter
      if (filters.spaceId && gathering.space?.id !== filters.spaceId) {
        return false;
      }

      // Apply date range filter based on selected filter type
      if (filters.fromDate || filters.toDate) {
        let dateToCompare: Date;
        
        if (filters.dateFilterType === 'submission') {
          // Filter by submission date (created_at)
          if (!gathering.created_at) {
            return false; // Exclude gatherings without created_at if filtering by submission date
          }
          dateToCompare = new Date(gathering.created_at);
        } else {
          // Filter by gathering date (default)
          dateToCompare = new Date(gathering.date);
        }
        
        if (filters.fromDate && dateToCompare < filters.fromDate) {
          return false;
        }
        
        if (filters.toDate) {
          // Set time to end of day for toDate comparison
          const endOfDay = new Date(filters.toDate);
          endOfDay.setHours(23, 59, 59, 999);
          
          if (dateToCompare > endOfDay) {
            return false;
          }
        }
      }

      // Apply search filter
      if (filters.searchQuery && filters.searchQuery.trim() !== '') {
        const query = filters.searchQuery.toLowerCase().trim();
        
        // Search in gathering title
        const titleMatch = gathering.title?.toLowerCase().includes(query);
        
        // Search in owner names
        let ownerMatch = false;
        if (gathering.owner) {
          const firstName = gathering.owner.first_name?.toLowerCase() || '';
          const lastName = gathering.owner.last_name?.toLowerCase() || '';
          const displayName = gathering.owner.display_name?.toLowerCase() || '';
          const fullName = `${firstName} ${lastName}`.trim();
          
          ownerMatch = 
            firstName.includes(query) ||
            lastName.includes(query) ||
            fullName.includes(query) ||
            displayName.includes(query);
        }
        
        // Return true if either title or owner matches
        if (!titleMatch && !ownerMatch) {
          return false;
        }
      }

      // Gathering passed all filters
      return true;
    });
  }, [filters]);

  // Create a filtered gatherings getter function
  const getFilteredGatherings = useCallback((gatherings: Gathering[]): Gathering[] => {
    return applyFilters(gatherings);
  }, [applyFilters]);

  // Return everything needed by components
  return {
    filters,
    setSpaceFilter,
    setDateRangeFilter,
    setSearchFilter,
    resetFilters,
    getFilteredGatherings,
    // For debugging and direct access if needed
    setFilters
  };
};

export default useFilters;